import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@listto/shared/solid";
import {
  calculateCompletionPercentage,
  cn,
  getListsCountQuery,
  getTodoStatsQuery,
  mapTodoStats,
  queryKeys,
} from "@listto/shared";
import { BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";
import { BarChart, type ChartSegment, DonutChart } from "@/components/ui/mini-chart";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const stats = createPowerSyncQuery({
    queryKey: () => queryKeys.todos.stats(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodoStatsQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const lists = createPowerSyncQuery({
    queryKey: () => queryKeys.lists.count(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getListsCountQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const listsCount = () => lists.data?.[0]?.count ?? 0;

  const todoStats = () => mapTodoStats(stats.data?.[0]);
  const completionPct = () =>
    calculateCompletionPercentage(todoStats().completed, todoStats().total);
  const pendingCount = () => Math.max(0, todoStats().total - todoStats().completed);

  const donutSegments = (): ChartSegment[] => [
    { label: t(msg`Completed`), value: todoStats().completed, color: "var(--success)" },
    { label: t(msg`Pending`), value: pendingCount(), color: "var(--warning)" },
  ];

  const barSegments = (): ChartSegment[] => [
    { label: t(msg`Completed`), value: todoStats().completed, color: "var(--success)" },
    { label: t(msg`Pending`), value: pendingCount(), color: "var(--warning)" },
    { label: t(msg`Important`), value: todoStats().important, color: "var(--accent)" },
    { label: t(msg`Lists`), value: listsCount(), color: "var(--muted-foreground)" },
  ];

  const donutSummary = () => {
    const { completed } = todoStats();
    const { total } = todoStats();
    const pct = completionPct();
    return t(msg`${completed} of ${total} tasks completed (${pct}%)`);
  };

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={t(msg`Total`)}
            value={todoStats().total}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Completed`)}
            value={todoStats().completed}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Important`)}
            value={todoStats().important}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Lists`)}
            value={listsCount()}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={lists.isPending}
          />
        </div>

        <Show when={todoStats().total > 0 && !stats.isPending}>
          <div class="grid grid-cols-1 items-center gap-6 sm:grid-cols-[auto_1fr]">
            {/* Completion donut: completed vs pending */}
            <div class="flex flex-col items-center gap-3">
              <DonutChart
                segments={donutSegments()}
                centerValue={`${completionPct()}%`}
                centerLabel={t(msg`Done`)}
                summary={donutSummary()}
              />
              <div class="flex items-center gap-4 text-xs">
                <span class="flex items-center gap-1.5">
                  <span class="h-2.5 w-2.5 rounded-full bg-success" />
                  <span class="text-muted-foreground">{t(msg`Completed`)}</span>
                </span>
                <span class="flex items-center gap-1.5">
                  <span class="h-2.5 w-2.5 rounded-full bg-warning" />
                  <span class="text-muted-foreground">{t(msg`Pending`)}</span>
                </span>
              </div>
            </div>

            {/* Breakdown bars */}
            <div class="space-y-3">
              <span class="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t(msg`Breakdown`)}
              </span>
              <BarChart
                segments={barSegments()}
                summary={barSegments()
                  .map((s) => `${s.label}: ${s.value}`)
                  .join(", ")}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
